import { mastodonTagDisplayName, normalizeMastodonTagName } from '@/lib/mastodonTag'

/**
 * Pure helpers for followed-hashtag attribution on the webapp. Deliberately
 * free of React imports so the rules can be unit-tested directly.
 */

/** Structural shape the pure attribution helpers need. */
export interface AttributionPost {
    mastodonTimelineStatusId?: string
    mastodonTimelineAccountId?: string
    mastodonTags?: string[]
}

export const MAX_BANNER_TAGS = 3

/**
 * Diff a post's content hashtags against the viewer's followed-hashtag names.
 *
 * Returns a map keyed by `mastodonTimelineStatusId` (NOT `post.uri` — two
 * timeline entries that are boosts of the same original post share a URI but
 * are distinct entries with distinct delivering accounts). Values are the
 * original-cased, deduped, order-preserved matched tag names (no leading `#`).
 *
 * Excludes posts with no timeline status/account id (non-Mastodon or nested
 * normalizations) and the viewer's own posts. Malformed tag names that
 * normalize to an empty string are dropped before matching.
 */
export function computeHashtagMatches(
    posts: ReadonlyArray<AttributionPost>,
    followedTagNames: ReadonlyArray<string>,
    viewerAccountId?: string
): Map<string, string[]> {
    const followed = new Set(
        followedTagNames.map(normalizeMastodonTagName).filter(name => name.length > 0)
    )
    const matches = new Map<string, string[]>()
    if (followed.size === 0) return matches

    for (const post of posts) {
        const statusId = post.mastodonTimelineStatusId
        const accountId = post.mastodonTimelineAccountId
        if (!statusId || !accountId) continue
        if (viewerAccountId !== undefined && accountId === viewerAccountId) continue

        const seen = new Set<string>()
        const matched: string[] = []
        for (const rawTag of post.mastodonTags ?? []) {
            const normalized = normalizeMastodonTagName(rawTag)
            if (!normalized || !followed.has(normalized) || seen.has(normalized)) continue
            seen.add(normalized)
            matched.push(mastodonTagDisplayName(rawTag).replace(/^#/, ''))
        }
        if (matched.length > 0) matches.set(statusId, matched)
    }
    return matches
}

/**
 * Distinct delivering-account ids for posts present in `matches` — the id list
 * fed to the chunked relationship fetch.
 */
export function candidateAccountIdsForAttribution(
    posts: ReadonlyArray<AttributionPost>,
    matches: ReadonlyMap<string, string[]>
): string[] {
    if (matches.size === 0) return []
    const seen = new Set<string>()
    const result: string[] = []
    for (const post of posts) {
        const statusId = post.mastodonTimelineStatusId
        if (!statusId || !matches.has(statusId)) continue
        const accountId = post.mastodonTimelineAccountId
        if (!accountId || seen.has(accountId)) continue
        seen.add(accountId)
        result.push(accountId)
    }
    return result
}

/**
 * Returns the same array reference when no post changes. Only posts whose
 * matched hashtags belong to an account the viewer is known NOT to follow
 * (`following === false`) get a new object with `viaFollowedHashtags`. Posts
 * with `following === true` or `undefined` (unresolved/unknown) pass through
 * untouched — fail-conservative and reference-preserving.
 */
export function applyHashtagAttribution<
    T extends AttributionPost & { viaFollowedHashtags?: string[] },
>(
    posts: ReadonlyArray<T>,
    matches: ReadonlyMap<string, string[]>,
    relationshipCache: (accountId: string) => boolean | undefined
): ReadonlyArray<T> {
    if (matches.size === 0) return posts

    let changed = false
    const next = posts.map(post => {
        const statusId = post.mastodonTimelineStatusId
        if (!statusId) return post
        const tags = matches.get(statusId)
        if (!tags || tags.length === 0) return post
        const accountId = post.mastodonTimelineAccountId
        if (!accountId) return post
        if (relationshipCache(accountId) !== false) return post
        changed = true
        return { ...post, viaFollowedHashtags: tags }
    })
    return changed ? next : posts
}

/** First `max` display tags plus the count of any remainder, for the banner. */
export function splitBannerTags(
    tags: string[],
    max = MAX_BANNER_TAGS
): { shown: string[]; extra: number } {
    if (tags.length <= max) return { shown: tags, extra: 0 }
    return { shown: tags.slice(0, max), extra: tags.length - max }
}

/** Sorted, deduped chunks of at most `size` account ids. */
export function chunkSortedIds(ids: string[], size = 40): string[][] {
    const sorted = [...new Set(ids)].sort()
    const chunks: string[][] = []
    for (let i = 0; i < sorted.length; i += size) {
        chunks.push(sorted.slice(i, i + size))
    }
    return chunks
}
